import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Truck, LogOut, Loader2, Phone, Mail, CalendarDays, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/constants/site";
import { useAuth } from "@/context/AuthContext";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const { user, loading, login, logout } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    if (loading || !user) return;
    if (!user.is_admin) {
      setForbidden(true);
      setFetching(false);
      return;
    }
    const load = async () => {
      try {
        const [q, b] = await Promise.all([
          fetch(`${API}/quotes`, { credentials: "include" }),
          fetch(`${API}/bookings`, { credentials: "include" }),
        ]);
        if (q.status === 403 || b.status === 403) {
          setForbidden(true);
          return;
        }
        if (!q.ok || !b.ok) throw new Error("fetch failed");
        setQuotes(await q.json());
        setBookings(await b.json());
      } catch {
        setForbidden(true);
      } finally {
        setFetching(false);
      }
    };
    load();
  }, [user, loading]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-slate-50 px-6" data-testid="admin-login-gate">
        <Truck className="h-10 w-10 text-orange-500" />
        <h1 className="text-center font-display text-3xl font-semibold text-slate-900">Area riservata</h1>
        <p className="max-w-sm text-center text-slate-600">Accedi con il tuo account Google autorizzato per vedere richieste e prenotazioni.</p>
        <Button
          onClick={login}
          data-testid="admin-login-button"
          className="rounded-md bg-orange-500 px-8 font-semibold text-white hover:bg-orange-600"
        >
          Accedi con Google
        </Button>
        <a href="/" className="text-sm text-slate-500 underline" data-testid="admin-back-home">Torna al sito</a>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-slate-50 px-6" data-testid="admin-forbidden">
        <h1 className="text-center font-display text-3xl font-semibold text-slate-900">Accesso non autorizzato</h1>
        <p className="max-w-sm text-center text-slate-600">
          L'account {user.email} non ha accesso all'area riservata.
        </p>
        <Button variant="outline" onClick={logout} data-testid="admin-forbidden-logout">Esci</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-orange-500 text-white">
              <Truck className="h-5 w-5" />
            </span>
            <span className="font-display text-xl font-semibold text-slate-900">Area riservata</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-slate-500 sm:block">{user.email}</span>
            <Button variant="outline" size="sm" onClick={() => navigate("/")} data-testid="admin-home-button">Sito</Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout-button">
              <LogOut className="mr-1 h-4 w-4" /> Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-12 px-6 py-10">
        <section data-testid="admin-quotes-section">
          <h2 className="flex items-center gap-2 font-display text-2xl font-semibold text-slate-900">
            <FileText className="h-6 w-6 text-orange-500" /> Richieste di preventivo ({quotes.length})
          </h2>
          {fetching ? (
            <Loader2 className="mt-6 h-6 w-6 animate-spin text-orange-500" />
          ) : quotes.length === 0 ? (
            <p className="mt-4 text-slate-500" data-testid="admin-quotes-empty">Nessuna richiesta per ora.</p>
          ) : (
            <div className="mt-6 overflow-x-auto rounded-lg border border-slate-200 bg-white">
              <table className="w-full text-left text-sm" data-testid="admin-quotes-table">
                <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-5 py-3">Data</th>
                    <th className="px-5 py-3">Nome</th>
                    <th className="px-5 py-3">Telefono</th>
                    <th className="px-5 py-3">Messaggio</th>
                    <th className="px-5 py-3">Account</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {quotes.map((q) => (
                    <tr key={q.id} className="align-top">
                      <td className="whitespace-nowrap px-5 py-3 text-slate-500">{fmtDate(q.created_at)}</td>
                      <td className="px-5 py-3 font-medium text-slate-900">{q.nome}</td>
                      <td className="px-5 py-3">
                        <a href={`tel:${q.telefono}`} className="flex items-center gap-1 text-orange-600">
                          <Phone className="h-3.5 w-3.5" /> {q.telefono}
                        </a>
                      </td>
                      <td className="max-w-xs px-5 py-3 text-slate-600">{q.messaggio}</td>
                      <td className="px-5 py-3 text-slate-500">{q.user_email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{q.user_email}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section data-testid="admin-bookings-section">
          <h2 className="flex items-center gap-2 font-display text-2xl font-semibold text-slate-900">
            <CalendarDays className="h-6 w-6 text-orange-500" /> Sopralluoghi prenotati ({bookings.length})
          </h2>
          {fetching ? (
            <Loader2 className="mt-6 h-6 w-6 animate-spin text-orange-500" />
          ) : bookings.length === 0 ? (
            <p className="mt-4 text-slate-500" data-testid="admin-bookings-empty">Nessuna prenotazione per ora.</p>
          ) : (
            <div className="mt-6 overflow-x-auto rounded-lg border border-slate-200 bg-white">
              <table className="w-full text-left text-sm" data-testid="admin-bookings-table">
                <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-5 py-3">Richiesta il</th>
                    <th className="px-5 py-3">Data sopralluogo</th>
                    <th className="px-5 py-3">Nome</th>
                    <th className="px-5 py-3">Telefono</th>
                    <th className="px-5 py-3">Note</th>
                    <th className="px-5 py-3">Account</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {bookings.map((b) => (
                    <tr key={b.id} className="align-top">
                      <td className="whitespace-nowrap px-5 py-3 text-slate-500">{fmtDate(b.created_at)}</td>
                      <td className="whitespace-nowrap px-5 py-3 font-semibold text-slate-900">{b.data}</td>
                      <td className="px-5 py-3 font-medium text-slate-900">{b.nome}</td>
                      <td className="px-5 py-3">
                        <a href={`tel:${b.telefono}`} className="flex items-center gap-1 text-orange-600">
                          <Phone className="h-3.5 w-3.5" /> {b.telefono}
                        </a>
                      </td>
                      <td className="max-w-xs px-5 py-3 text-slate-600">{b.note}</td>
                      <td className="px-5 py-3 text-slate-500">{b.user_email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
